import React, { Children, Component } from "react"
import { withRouter, RouteComponentProps } from "react-router-dom"
import DataGrid from "../../components/DataGrid"
import TabScreen from "../../components/TabScreen"
import CollapsableCard, { CollapsableCardBody, CollapsableCardHeader } from "../../components/CollapsableCard/CollapsableCard"
import { Column } from "../../components/DataGrid/types/types"
import { SprintsRest } from "../../services/sprints/sprints-rest-interface"
import { alphanumericCompare } from "../../services/utils/sort-util"
import { IUserSprint, SprintStatus } from "./types"
import SprintQuestionResults from "./SprintQuestionResults"
import meta from "./meta"
import "./index.css"

interface Props {

}

interface State {
    modalVisible: boolean
    subject: string
    status: string
    subcategory?: string
    allUserSprints?: IUserSprint[]
}


type AllProps = Props & RouteComponentProps<any>

export class SprintsScene extends Component<AllProps, State> {
    static instance: SprintsScene
    static latestViewedTestId?: string

    constructor(props) {
        super(props)

        this.state = {
            modalVisible: false,
            subject: "English",
            status: "new",
        }

        SprintsScene.instance = this
    }

    async componentDidMount() {
        let allUserSprints = await SprintsRest.getAllUserSprints()
        this.setState({allUserSprints})
    }

    componentWillUnmount() {
    }

    handleModalClose = () => {
        this.setState({modalVisible: false})
    }

    static startOrResumeSprintsTest(testId, returnTab: string, returnCardKey: string) {
        let instance = SprintsScene.instance
        let testLink = `/app/sprints/test/${testId}`
        SprintsScene.latestViewedTestId = testId
        instance.props.history.push(testLink, {returnTab, returnCardKey})
    }

    render() {
        let {modalVisible, subject, status, subcategory, allUserSprints} = this.state
        let routeState = this.props.location.state as {returnCardKey?: string} | undefined
        let returnCardKey = routeState && routeState.returnCardKey
        let screens = [
            {name: "English", screen: <SprintsScreen subject="English" allUserSprints={allUserSprints} returnCardKey={returnCardKey} />},
            {name: "Math",    screen: <SprintsScreen subject="Math"    allUserSprints={allUserSprints} returnCardKey={returnCardKey} />},
            {name: "Reading", screen: <SprintsScreen subject="Reading" allUserSprints={allUserSprints} returnCardKey={returnCardKey} />},
            {name: "Science", screen: <SprintsScreen subject="Science" allUserSprints={allUserSprints} returnCardKey={returnCardKey} />},
        ]

        return (
            <TabScreen title="Timed Sprints" screens={screens}>
            </TabScreen>
        )
    }
}

const ConnectedSprintsScene = withRouter(SprintsScene)

export default (props) => {
    return (
        <ConnectedSprintsScene {...props} />
    )
}

//--- Helpers ---

interface ISprintsScreen {
    subject: string // English | Math | Reading | Science
    allUserSprints?: IUserSprint[]
    returnCardKey?: string
}

function SprintsScreen(props: ISprintsScreen) {
    let {subject, allUserSprints, returnCardKey} = props
    let subjectKey = subject.toLowerCase()
    let sprints = meta.subjects[subjectKey]

    return (
        <div className="sprints-screen">
            <SprintsCardsView subject={subject} sprints={sprints} allUserSprints={allUserSprints} returnCardKey={returnCardKey}/>
        </div>
    )
}

function SprintsCardsView(props) {
    let {subject, sprints, allUserSprints, returnCardKey} = props

    return (
        <div className="sprints-cards-container">
            { sprints.map((sprintInfo, idx) => {
                let setNumber = idx + 1
                return (
                    <CollapsableInfoCardForSprints
                        subject={subject}
                        setNumber={setNumber}
                        sprintInfo={sprintInfo}
                        allUserSprints={allUserSprints}
                        returnCardKey={returnCardKey}
                    />
                )
            })}
        </div>
    )
}


const sortMethod = alphanumericCompare

const columns: Column[] = [
    {
        title: "SPRINT",
        dataProperty: "sprint",
        sortable: false,
        width: "25%",
    },
    {
        title: "STATUS",
        dataProperty: "status",
        sortable: false,
        width: "20%",
    },
    {
        title: "REVIEW",
        dataProperty: "review",
        sortable: false,
    },
]

function getTimedSprintId(subject, sprint) {
    let testId = `act-${subject.toLowerCase()}-${sprint}`
    return testId
}

function getSprintTestStatus(testId, allUserSprints): SprintStatus {
    if (allUserSprints) {
        let found = allUserSprints.find(s => s.timedSprintId === testId)
        if (found) {
            let {numberOfQuestions, answers} = found
            let nofAnswers = answers.length
            if (found.remainingTimeInSeconds <= 0) {
                return SprintStatus.COMPLETED
            }
            return SprintStatus.RESUME
        }
    }
    return SprintStatus.START
}

function getStatusButton(subject, setNumber, sprint, allUserSprints: IUserSprint[]) {
    let testId = getTimedSprintId(subject, sprint)
    let status = getSprintTestStatus(testId, allUserSprints)
    let resume = status === "RESUME"
    let completed = status === "COMPLETED" 
    let className = `btn btn--bg-${completed ? "purple": resume ? "green": "blue"} p-0 w-100`
    let title = status
    let disabled = completed
    let action = () => { SprintsScene.startOrResumeSprintsTest(testId, subject, `${subject}-${setNumber}`)}

    return (
        <button 
            className={className}
            disabled={disabled}
            onClick={action}>
                {title}
        </button>
    )
}

function getSprintData(subject, setNumber, sprintNumber, allUserSprints: IUserSprint[]) {
    let id = `sprint-data-${subject}-${setNumber}`
    let button = getStatusButton(subject, setNumber, sprintNumber, allUserSprints)
    let testId = getTimedSprintId(subject, sprintNumber)
    let userSprint = (allUserSprints || []).find(s => s.timedSprintId === testId)
    let review = userSprint && getSprintTestStatus(testId, allUserSprints) === SprintStatus.COMPLETED
        ? <SprintQuestionResults key={testId} testId={testId} answers={userSprint.answers} />
        : null

    let data = {
        sprint: {
            content: `Timed Sprint ${sprintNumber}`,
            comparator: "",
        },
        status: {
            content: () => button,
            comparator: "",
        },
        review: {
            content: () => review,
            comparator: "",
        },
    }

    return {
        id,
        data
    }
}

function getSprintsData(subject: string, setNumber: number, sprintNumbers: number[], allUserSprints: IUserSprint[]) {
    let data = sprintNumbers.map(sprintNumber => getSprintData(subject, setNumber, sprintNumber, allUserSprints))
    return data
}

function CollapsableInfoCardForSprints({subject, setNumber, sprintInfo, allUserSprints, returnCardKey}) {
    let {title, sprints} =  sprintInfo
    let ribbonColor = "darkPurple"
    let cardKey = `${subject}-${setNumber}`
    let collapsed = returnCardKey !== cardKey
    let data = getSprintsData(subject, setNumber, sprints, allUserSprints)

    return (
        <CollapsableCard key={title} initiallyCollapsed={collapsed}>
            <CollapsableCardHeader>
                <div className="lesson-list-card-header">
                    <span className={`ribbon ${ribbonColor}`}>
                        <span className="bellringers-grade-level">{setNumber}</span>
                    </span>
                    <h2 className="lesson-list-card-header--text">{title}</h2>
                </div>
            </CollapsableCardHeader>
             <CollapsableCardBody>
                <DataGrid
                    columns={columns}
                    data={data}
                    className="lessons-table"
                />
             </CollapsableCardBody>
        </CollapsableCard>
    )
}
